import React, { useCallback, useEffect, useRef, useState } from 'react'
import { glossaryService, GlossaryTerm } from '../lib/cat/glossaryService'
import { Plus, Download, Trash2, Pencil, Check, X } from 'lucide-react'

const PAGE_SIZE = 50
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pt-PT', label: 'Portuguese (PT)' },
  { code: 'pt-br', label: 'Portuguese (BR)' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'ar', label: 'Arabic' },
]

export default function GlossaryPage() {
  const [terms, setTerms] = useState<GlossaryTerm[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [srcLang, setSrcLang] = useState('en')
  const [tgtLang, setTgtLang] = useState('pt-PT')
  const [editId, setEditId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newTerm, setNewTerm] = useState({
    term_source: '', term_target: '', definition: '', domain: '', notes: ''
  })
  const [editValues, setEditValues] = useState<Partial<GlossaryTerm>>({})
  const searchDebounce = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0)
    }, 400)
    return () => clearTimeout(searchDebounce.current)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    const result = await glossaryService.getTerms(srcLang, tgtLang, debouncedSearch, page, PAGE_SIZE)
    setTerms(result.terms)
    setCount(result.count)
    setLoading(false)
  }, [srcLang, tgtLang, debouncedSearch, page])

  useEffect(() => { load() }, [load])

  const handleAdd = async () => {
    if (!newTerm.term_source.trim() || !newTerm.term_target.trim()) return
    await glossaryService.addTerm(
      newTerm.term_source, newTerm.term_target, srcLang, tgtLang,
      newTerm.definition, newTerm.domain, newTerm.notes
    )
    setNewTerm({ term_source: '', term_target: '', definition: '', domain: '', notes: '' })
    setShowAddForm(false)
    load()
  }

  const handleSaveEdit = async (id: string) => {
    await glossaryService.updateTerm(id, editValues)
    setEditId(null)
    load()
  }

  const handleDelete = async (id: string) => {
    await glossaryService.deleteTerm(id)
    setDeleteConfirm(null)
    load()
  }

  const handleExport = async () => {
    const all = await glossaryService.getAllTerms(srcLang, tgtLang)
    const csv = glossaryService.exportCSV(all)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Glossary_${srcLang}_${tgtLang}_${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const totalPages = Math.ceil(count / PAGE_SIZE)

  return (
    <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Glossary</h1>
            <p className="text-gray-700 text-sm mt-1">
              {count.toLocaleString()} term{count !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport} disabled={count === 0}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-40 transition-colors">
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-gold-600 hover:bg-gold-500 text-white transition-colors">
              <Plus className="w-4 h-4" />
              Add Term
            </button>
          </div>
        </div>

        {/* Language pair selector */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <label className="block text-xs text-gray-700 mb-1">Source language</label>
              <select value={srcLang} onChange={e => { setSrcLang(e.target.value); setPage(0) }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500">
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div className="text-gray-800 mt-4">→</div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">Target language</label>
              <select value={tgtLang} onChange={e => { setTgtLang(e.target.value); setPage(0) }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500">
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-700 mb-1">Search</label>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search terms…"
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500" />
            </div>
          </div>
        </div>

        {/* Add term form */}
        {showAddForm && (
          <div className="bg-gold-50 border border-gold-200 rounded-xl p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Add New Term</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-700 mb-1">Source term *</label>
                <input type="text" value={newTerm.term_source}
                  onChange={e => setNewTerm(p => ({ ...p, term_source: e.target.value }))}
                  placeholder="Source term"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Target term *</label>
                <input type="text" value={newTerm.term_target}
                  onChange={e => setNewTerm(p => ({ ...p, term_target: e.target.value }))}
                  placeholder="Target term"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Domain</label>
                <input type="text" value={newTerm.domain}
                  onChange={e => setNewTerm(p => ({ ...p, domain: e.target.value }))}
                  placeholder="e.g. Legal, Medical, Technical"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Definition</label>
                <input type="text" value={newTerm.definition}
                  onChange={e => setNewTerm(p => ({ ...p, definition: e.target.value }))}
                  placeholder="Optional definition"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-sm rounded-lg bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleAdd}
                disabled={!newTerm.term_source.trim() || !newTerm.term_target.trim()}
                className="px-4 py-2 text-sm rounded-lg bg-gold-600 hover:bg-gold-500 text-white disabled:opacity-40 transition-colors">
                Add Term
              </button>
            </div>
          </div>
        )}

        {/* Terms table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center h-32 gap-3 text-gray-700">
              <div className="animate-spin w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full" />
              Loading terms…
            </div>
          ) : terms.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <span className="text-3xl">📖</span>
              <span className="text-gray-700 text-sm">
                {debouncedSearch ? 'No terms match your search' : 'No terms yet — add your first term above'}
              </span>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Source</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Target</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-32">Domain</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Definition</th>
                    <th className="w-20 px-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {terms.map(term => (
                    <tr key={term.id} className="hover:bg-gray-50 group">
                      <td className="px-4 py-3">
                        {editId === term.id ? (
                          <input value={editValues.term_source ?? term.term_source}
                            onChange={e => setEditValues(p => ({ ...p, term_source: e.target.value }))}
                            className="w-full px-2 py-1 border border-gold-400 rounded text-sm focus:outline-none" />
                        ) : (
                          <span className="font-medium text-gray-900">{term.term_source}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editId === term.id ? (
                          <input value={editValues.term_target ?? term.term_target}
                            onChange={e => setEditValues(p => ({ ...p, term_target: e.target.value }))}
                            className="w-full px-2 py-1 border border-gold-400 rounded text-sm focus:outline-none" />
                        ) : (
                          <span className="text-gray-700">{term.term_target}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editId === term.id ? (
                          <input value={editValues.domain ?? term.domain ?? ''}
                            onChange={e => setEditValues(p => ({ ...p, domain: e.target.value }))}
                            className="w-full px-2 py-1 border border-gold-400 rounded text-sm focus:outline-none" />
                        ) : term.domain ? (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                            {term.domain}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        {editId === term.id ? (
                          <input value={editValues.definition ?? term.definition ?? ''}
                            onChange={e => setEditValues(p => ({ ...p, definition: e.target.value }))}
                            className="w-full px-2 py-1 border border-gold-400 rounded text-sm focus:outline-none" />
                        ) : (
                          <span className="line-clamp-1">{term.definition}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editId === term.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleSaveEdit(term.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={() => setEditId(null)}
                              className="p-1 text-gray-800 hover:bg-gray-100 rounded transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : deleteConfirm === term.id ? (
                          <div className="flex items-center gap-1 text-xs">
                            <button onClick={() => handleDelete(term.id)}
                              className="text-red-600 font-medium hover:text-red-700">Yes</button>
                            <span className="text-gray-700">/</span>
                            <button onClick={() => setDeleteConfirm(null)}
                              className="text-gray-700 hover:text-gray-700">No</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditId(term.id); setEditValues({}) }}
                              className="p-1 text-gray-800 hover:text-gold-600 hover:bg-gold-50 rounded transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setDeleteConfirm(term.id)}
                              className="p-1 text-gray-800 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <span className="text-xs text-gray-700">
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, count)} of {count.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="px-3 py-1.5 text-xs rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-50">
                      ← Prev
                    </button>
                    <span className="text-xs text-gray-700">{page + 1} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                      className="px-3 py-1.5 text-xs rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-50">
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
  )
}
